import { requireSupabase } from './client';

export type AdminTable =
  | 'site_settings'
  | 'site_pages'
  | 'press_releases'
  | 'press_categories'
  | 'solution_markets'
  | 'solution_categories'
  | 'solutions'
  | 'media_assets'
  | 'events'
  | 'admin_profiles';

export interface AdminTableConfig {
  table: AdminTable;
  label: string;
  description: string;
  titleField: string;
  conflictField: string;
  orderField?: string;
  jsonFields: string[];
  readonlyFields: string[];
  template: Record<string, unknown>;
}

export const adminTables: AdminTableConfig[] = [
  {
    table: 'press_releases',
    label: 'Press releases',
    description: 'Anúncios, comunicados e conteúdo da sala de imprensa.',
    titleField: 'slug',
    conflictField: 'slug',
    orderField: 'release_date',
    jsonFields: ['translations', 'pdf_files', 'metadata'],
    readonlyFields: ['id', 'created_at', 'updated_at'],
    template: {
      slug: 'novo-press-release',
      status: 'draft',
      release_date: new Date().toISOString().slice(0, 10),
      category: 'Release',
      author: 'TechEnabler',
      featured: false,
      cover_image: '',
      translations: {
        pt: { title: '', excerpt: '', body: '', seo_title: '', seo_description: '' },
        es: { title: '', excerpt: '', body: '', seo_title: '', seo_description: '' },
      },
      pdf_files: {},
      metadata: {},
    },
  },
  {
    table: 'press_categories',
    label: 'Categorias de Imprensa',
    description: 'Categorias usadas nos press releases (nome em pt/es/en).',
    titleField: 'slug',
    conflictField: 'slug',
    orderField: 'sort_order',
    jsonFields: ['translations', 'metadata'],
    readonlyFields: ['id', 'created_at', 'updated_at'],
    template: {
      slug: 'nova-categoria',
      status: 'published',
      sort_order: 0,
      translations: {
        pt: { title: '', description: '' },
        es: { title: '', description: '' },
        en: { title: '', description: '' },
      },
      metadata: {},
    },
  },
  {
    table: 'solutions',
    label: 'Soluções',
    description: 'Datasheets e páginas individuais de soluções.',
    titleField: 'slug',
    conflictField: 'slug',
    orderField: 'sort_order',
    jsonFields: ['translations', 'target_audience', 'benefits', 'technical', 'metadata'],
    readonlyFields: ['id', 'created_at', 'updated_at'],
    template: {
      slug: 'nova-solucao',
      sku: 'TE-DATASHEET',
      status: 'draft',
      market_id: null,
      scope: null,
      category_label: '',
      featured: false,
      cover_image: '',
      sort_order: 0,
      // Os tres idiomas ja nascem no template para que o editor abra com as abas
      // PT/ES/EN preenchiveis. Campo vazio nao "apaga" o idioma: pickLocalizedValue
      // trata string vazia como ausente e cai no PT.
      translations: {
        pt: { title: '', subtitle: '', tagline: '', short_description: '', body: '', seo_title: '', seo_description: '' },
        es: { title: '', subtitle: '', tagline: '', short_description: '', body: '', seo_title: '', seo_description: '' },
        en: { title: '', subtitle: '', tagline: '', short_description: '', body: '', seo_title: '', seo_description: '' },
      },
      target_audience: { pt: {}, es: {} },
      benefits: { pt: [], es: [] },
      technical: { pt: {}, es: {} },
      commercial_models: [],
      metadata: {},
    },
  },
  {
    table: 'events',
    label: 'Eventos',
    description: 'Agenda e presença institucional.',
    titleField: 'slug',
    conflictField: 'slug',
    orderField: 'starts_on',
    jsonFields: ['translations', 'metadata'],
    readonlyFields: ['id', 'created_at', 'updated_at'],
    template: {
      slug: 'novo-evento',
      status: 'draft',
      starts_on: new Date().toISOString().slice(0, 10),
      ends_on: null,
      location: '',
      cover_image: '',
      sort_order: 0,
      translations: { pt: { title: '', description: '' }, es: { title: '', description: '' } },
      metadata: {},
    },
  },
  {
    table: 'admin_profiles',
    label: 'Administradores',
    description: 'Usuários com permissão de administração.',
    titleField: 'email',
    conflictField: 'user_id',
    jsonFields: [],
    readonlyFields: ['created_at', 'updated_at'],
    template: {
      user_id: '',
      email: '',
      display_name: '',
      role: 'editor',
    },
  },
];

export async function getSession() {
  return requireSupabase().auth.getSession();
}

export async function signInAdmin(email: string, password: string) {
  return requireSupabase().auth.signInWithPassword({ email, password });
}

export async function signOutAdmin() {
  return requireSupabase().auth.signOut();
}

const LAST_ACTIVITY_KEY = 'techenabler_admin_last_activity';
const INACTIVITY_LIMIT_MS = 24 * 60 * 60 * 1000; // 24h sem voltar ao admin => sessao expira

/** Marca "agora" como a ultima vez que essa sessao foi validada (login, abrir o painel, voltar pra aba). */
export function touchAdminActivity() {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

export function clearAdminActivity() {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

/** Sem registro anterior (primeiro acesso do navegador) nao conta como expirado. */
export function isAdminSessionExpired() {
  const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  if (!last) return false;
  return Date.now() - last > INACTIVITY_LIMIT_MS;
}

/** Troca a senha do proprio usuario autenticado (nao precisa de service role). */
export async function updateOwnPassword(newPassword: string) {
  return requireSupabase().auth.updateUser({ password: newPassword });
}

export async function claimFirstAdmin() {
  return requireSupabase().rpc('claim_first_admin');
}

export async function isCurrentUserAdmin() {
  const { data, error } = await requireSupabase().from('admin_profiles').select('user_id, role').limit(1);
  if (error) return false;
  return Boolean(data?.length);
}

/** Nome de exibição do próprio usuário autenticado, para o "Olá, {nome}" do painel. */
export async function getOwnDisplayName(userId: string): Promise<string> {
  const { data, error } = await requireSupabase()
    .from('admin_profiles')
    .select('display_name')
    .eq('user_id', userId)
    .maybeSingle();
  if (error || !data?.display_name) return '';
  return data.display_name as string;
}

export interface CreateAdminUserResult {
  user_id: string;
  email: string;
  temp_password: string;
}

/**
 * Cria a conta de login (Supabase Auth) e o registro em admin_profiles em um
 * unico passo, via edge function `create-admin-user`. Resolve a limitacao
 * anterior da tela de Administradores, que exigia colar manualmente o UUID
 * de um usuario ja existente em Authentication -> Users. A function roda
 * com service_role no servidor (nunca exposta ao browser) e so aceita a
 * chamada de quem ja e admin (owner ou editor) do proprio painel.
 */
export async function createAdminUser(input: {
  email: string;
  display_name: string;
  role: 'owner' | 'editor';
}): Promise<{ data: CreateAdminUserResult | null; error: { message: string } | null }> {
  const { data, error } = await requireSupabase().functions.invoke('create-admin-user', {
    body: input,
  });

  if (error) {
    // Em erro (status != 2xx) o supabase-js nao expoe o body automaticamente:
    // o JSON com a mensagem real ("error": "...") fica em error.context (Response).
    let message = error.message || 'Falha ao criar administrador.';
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const body = await context.clone().json();
        if (body?.error) message = body.error;
      } catch {
        // resposta sem corpo JSON legivel: mantem a mensagem generica acima.
      }
    }
    return { data: null, error: { message } };
  }

  if ((data as { error?: string } | null)?.error) {
    return { data: null, error: { message: (data as { error: string }).error } };
  }

  return { data: data as CreateAdminUserResult, error: null };
}

export async function listAdminRows(config: AdminTableConfig) {
  let query = requireSupabase().from(config.table).select('*').limit(500);
  if (config.orderField) {
    query = query.order(config.orderField, { ascending: config.orderField !== 'release_date' && config.orderField !== 'starts_on' && config.orderField !== 'created_at' });
  }
  return query;
}

// Tabelas que possuem trilha de auditoria (created_by/updated_by/published_at).
// Ver requisito 6: Autor (campo de conteudo editavel) e SEPARADO desta trilha.
const AUDITED_TABLES: AdminTable[] = ['press_releases', 'solutions', 'events'];

/**
 * Popula created_by/updated_by com o uuid do usuario autenticado em TODO save,
 * e published_at apenas quando o status transiciona para 'published' e o campo
 * ainda esta vazio (nao sobrescreve uma publicacao anterior em edicoes seguintes).
 *
 * Decisao de design (requisito 6): gravamos o uuid (auth.users.id) nas colunas
 * created_by/updated_by, ja que sao FKs para auth.users. Para exibir um nome
 * legivel na UI sem depender de join complexo no client, a resolucao uuid ->
 * email/display_name e feita por `resolveAdminIdentities`, que chama a RPC
 * `resolve_admin_identity` (cria em admin_profiles). Isso evita duplicar dados
 * de identidade em cada linha de conteudo e mantem uma unica fonte de verdade
 * (admin_profiles). O campo "Autor" continua 100% independente: nunca e lido
 * nem escrito por esta funcao.
 */
async function withAuditTrail(config: AdminTableConfig, row: Record<string, unknown>, isNewRecord: boolean) {
  if (!AUDITED_TABLES.includes(config.table)) return row;

  const { data } = await getSession();
  const userId = data.session?.user?.id || null;
  if (!userId) return row;

  const next = { ...row };
  next.updated_by = userId;
  if (isNewRecord || !next.created_by) {
    next.created_by = userId;
  }

  const previousStatus = (row as { __previousStatus?: string }).__previousStatus;
  const nextStatus = next.status;
  const transitioningToPublished = nextStatus === 'published' && previousStatus !== 'published';

  if (transitioningToPublished && !next.published_at) {
    next.published_at = new Date().toISOString();
  }

  delete (next as Record<string, unknown>).__previousStatus;
  return next;
}

export async function upsertAdminRow(config: AdminTableConfig, row: Record<string, unknown>, options?: { isNewRecord?: boolean }) {
  let cleaned = { ...row };
  const isNewRecord = options?.isNewRecord ?? !cleaned.created_at;
  cleaned = await withAuditTrail(config, cleaned, isNewRecord);

  // __previousStatus e um campo de controle interno (AdminDashboard anexa em todo
  // save, nao so nas tabelas com trilha de auditoria) e nunca deve ir pro banco.
  // withAuditTrail so o remove para tabelas em AUDITED_TABLES; para as demais
  // (ex.: events) ele passava direto pro upsert e o PostgREST rejeitava com
  // "Could not find the '__previousStatus' column of 'events' in the schema cache".
  delete (cleaned as Record<string, unknown>).__previousStatus;

  config.readonlyFields.forEach((field) => delete cleaned[field]);
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === '') cleaned[key] = null;
  });

  // Apenas um press release pode ficar em destaque por vez. Sem isso, marcar um
  // novo destaque nao desmarca o anterior, e sortPressRows (que poe featured=true
  // no topo do proprio ano antes de olhar a data) deixa o release antigo preso
  // na frente de releases mais recentes do mesmo ano (foi o que aconteceu com o
  // release da Altarede, marcado featured em 2025 e nunca desmarcado).
  if (config.table === 'press_releases' && cleaned.featured === true && cleaned.slug) {
    const { error: featuredError } = await requireSupabase()
      .from('press_releases')
      .update({ featured: false })
      .neq('slug', cleaned.slug as string)
      .eq('featured', true);
    if (featuredError) {
      throw new Error(`Falha ao desmarcar destaque anterior: ${featuredError.message}`);
    }
  }

  return requireSupabase()
    .from(config.table)
    .upsert(cleaned as never, { onConflict: config.conflictField })
    .select()
    .single();
}

/**
 * Verifica se um slug ja esta em uso por OUTRO registro na mesma tabela
 * (exclui o proprio registro em edicao comparando por id).
 * Usado no requisito 7 para avisar o usuario antes de salvar.
 */
export async function checkSlugAvailability(table: AdminTable, slug: string, excludeId?: string | null) {
  if (!slug) return { available: true, existingSlugs: [] as string[] };

  let query = requireSupabase().from(table).select('id, slug').eq('slug', slug);
  if (excludeId) query = query.neq('id', excludeId);
  const { data, error } = await query;
  if (error) return { available: true, existingSlugs: [] as string[] };

  return { available: !data || data.length === 0, existingSlugs: (data || []).map((r: any) => String(r.slug)) };
}

/** Busca todos os slugs de uma tabela (usado para gerar variantes com uniqueSlug). */
export async function listSlugs(table: AdminTable, excludeId?: string | null) {
  let query = requireSupabase().from(table).select('id, slug');
  if (excludeId) query = query.neq('id', excludeId);
  const { data, error } = await query;
  if (error || !data) return [] as string[];
  return data.map((r: any) => String(r.slug || ''));
}

/** Resolve uuids de created_by/updated_by para email/nome legivel via RPC (requisito 6). */
export async function resolveAdminIdentities(userIds: string[]): Promise<Record<string, { email: string | null; display_name: string | null }>> {
  const unique = Array.from(new Set(userIds.filter(Boolean)));
  if (!unique.length) return {};

  const { data, error } = await requireSupabase().rpc('resolve_admin_identity', { user_ids: unique });
  if (error || !data) return {};

  const map: Record<string, { email: string | null; display_name: string | null }> = {};
  (data as any[]).forEach((entry) => {
    map[entry.user_id] = { email: entry.email, display_name: entry.display_name };
  });
  return map;
}

export async function deleteAdminRow(config: AdminTableConfig, row: Record<string, unknown>) {
  if ('id' in row && row.id) {
    return requireSupabase().from(config.table).delete().eq('id', row.id as string);
  }

  if ('slug' in row && row.slug) {
    return requireSupabase().from(config.table).delete().eq('slug', row.slug as string);
  }

  if ('key' in row && row.key) {
    return requireSupabase().from(config.table).delete().eq('key', row.key as string);
  }

  if ('user_id' in row && row.user_id) {
    return requireSupabase().from(config.table).delete().eq('user_id', row.user_id as string);
  }

  throw new Error('Não foi possível identificar a chave do registro.');
}

export const PRESS_KIT_MEDIA_KEY = 'press_kit_media';

export interface PressKitTranslation {
  title: string;
  description: string;
  button_label: string;
}

export interface PressKitMediaItem {
  id: string;
  file_type: string;
  file_url: string | null;
  translations: {
    pt: PressKitTranslation;
    es: PressKitTranslation;
    en: PressKitTranslation;
  };
}

const emptyTranslation = (): PressKitTranslation => ({ title: '', description: '', button_label: '' });

const pressKitDefaults: PressKitMediaItem[] = [
  {
    id: 'logos',
    file_type: 'ZIP / PNG / SVG',
    file_url: null,
    translations: {
      pt: { title: 'Logos TechEnabler', description: '', button_label: 'Download do kit' },
      es: { title: 'Logos TechEnabler', description: '', button_label: 'Descargar kit' },
      en: { title: 'TechEnabler Logos', description: '', button_label: 'Download kit' },
    },
  },
  {
    id: 'apresentacao-institucional',
    file_type: 'PDF',
    file_url: null,
    translations: {
      pt: { title: 'Apresentação Institucional 2026', description: '', button_label: 'Download do PDF' },
      es: { title: 'Presentación Institucional 2026', description: '', button_label: 'Descargar PDF' },
      en: { title: 'Corporate Presentation 2026', description: '', button_label: 'Download PDF' },
    },
  },
  {
    id: 'brand-guidelines',
    file_type: 'PDF',
    file_url: null,
    translations: {
      pt: { title: 'Brand Guidelines', description: '', button_label: 'Download do PDF' },
      es: { title: 'Brand Guidelines', description: '', button_label: 'Descargar PDF' },
      en: { title: 'Brand Guidelines', description: '', button_label: 'Download PDF' },
    },
  },
  {
    id: 'fotos-executivos',
    file_type: 'JPG',
    file_url: null,
    translations: {
      pt: { title: 'Fotos Oficiais Executivos', description: '', button_label: 'Download das fotos' },
      es: { title: 'Fotos Oficiales de Ejecutivos', description: '', button_label: 'Descargar fotos' },
      en: { title: 'Official Executive Photos', description: '', button_label: 'Download photos' },
    },
  },
];

export async function getPressKitMedia(): Promise<PressKitMediaItem[]> {
  const { data, error } = await requireSupabase()
    .from('site_settings')
    .select('value')
    .eq('key', PRESS_KIT_MEDIA_KEY)
    .maybeSingle();

  if (error || !data?.value) return pressKitDefaults;

  const items = (data.value as any)?.items;
  if (!Array.isArray(items) || !items.length) return pressKitDefaults;

  return items.map((item: any) => ({
    id: String(item.id || ''),
    file_type: String(item.file_type || ''),
    file_url: item.file_url || null,
    translations: {
      pt: { ...emptyTranslation(), ...(item.translations?.pt || {}) },
      es: { ...emptyTranslation(), ...(item.translations?.es || {}) },
      en: { ...emptyTranslation(), ...(item.translations?.en || {}) },
    },
  }));
}

export async function savePressKitMedia(items: PressKitMediaItem[]) {
  return requireSupabase()
    .from('site_settings')
    .upsert(
      {
        key: PRESS_KIT_MEDIA_KEY,
        description: 'Materiais de download da Sala de Imprensa (logos, apresentação, brand guidelines, fotos)',
        value: { items },
      } as never,
      { onConflict: 'key' },
    )
    .select()
    .single();
}

// --- Contatos institucionais (requisito 12) ---
// Mesmo padrao key/value em site_settings usado por Materiais de Imprensa.

export const SITE_CONTACTS_KEY = 'site_contacts';

export interface SiteContacts {
  address: { line1: string; line2: string };
  phones: string[];
  emails: { contact: string; commercial: string };
  press_contact: { name: string; email: string; phone: string };
  social: { linkedin: string; instagram: string };
  whatsapp: { number: string; message: string };
}

export const siteContactsDefaults: SiteContacts = {
  address: {
    line1: 'Rua Gomes de Carvalho, 1507 – andar 4 – sala 11',
    line2: 'Vila Olímpia – São Paulo/SP – 04547-005',
  },
  phones: ['+55 (11) 3042-1656', '+55 (11) 97658-9319'],
  emails: {
    contact: 'contato@techenabler.com.br',
    commercial: 'comercial@techenabler.com.br',
  },
  press_contact: {
    name: 'Silvia Helena Rodrigues',
    email: 'silvia.helena@techenabler.com.br',
    phone: '+55 (21) 96668-8444',
  },
  social: {
    linkedin: 'https://www.linkedin.com/company/techenabler-sistemas-tecnologia/',
    instagram: 'https://www.instagram.com/techenableroficial/',
  },
  whatsapp: {
    number: '5511976589319',
    message: '',
  },
};

export async function getSiteContacts(): Promise<SiteContacts> {
  const { data, error } = await requireSupabase()
    .from('site_settings')
    .select('value')
    .eq('key', SITE_CONTACTS_KEY)
    .maybeSingle();

  if (error || !data?.value) return siteContactsDefaults;

  const value = data.value as Partial<SiteContacts>;
  return {
    address: { ...siteContactsDefaults.address, ...(value.address || {}) },
    phones: Array.isArray(value.phones) && value.phones.length ? value.phones : siteContactsDefaults.phones,
    emails: { ...siteContactsDefaults.emails, ...(value.emails || {}) },
    press_contact: { ...siteContactsDefaults.press_contact, ...(value.press_contact || {}) },
    social: { ...siteContactsDefaults.social, ...(value.social || {}) },
    whatsapp: { ...siteContactsDefaults.whatsapp, ...(value.whatsapp || {}) },
  };
}

export async function saveSiteContacts(contacts: SiteContacts) {
  return requireSupabase()
    .from('site_settings')
    .upsert(
      {
        key: SITE_CONTACTS_KEY,
        description: 'Informações de contato institucional (endereço, telefones, e-mails, imprensa, redes sociais)',
        value: contacts,
      } as never,
      { onConflict: 'key' },
    )
    .select()
    .single();
}

// --- Suporte a dropdowns de Soluções (requisito 10) ---

export interface SolutionMarketOption {
  id: string;
  title: string;
}

export interface SolutionCategoryOption {
  id: string;
  market_id: string;
  scope: string;
  slug: string;
  title: string;
}

export async function listSolutionMarkets(): Promise<SolutionMarketOption[]> {
  const { data, error } = await requireSupabase()
    .from('solution_markets')
    .select('id, translations')
    .order('sort_order');
  if (error || !data) return [];
  return data.map((row: any) => ({ id: row.id, title: row.translations?.pt?.title || row.id }));
}

export async function listSolutionCategories(): Promise<SolutionCategoryOption[]> {
  const { data, error } = await requireSupabase()
    .from('solution_categories')
    .select('id, market_id, scope, slug, translations')
    .order('sort_order');
  if (error || !data) return [];
  return data.map((row: any) => ({
    id: row.id,
    market_id: row.market_id,
    scope: row.scope,
    slug: row.slug,
    title: row.translations?.pt?.title || row.slug,
  }));
}

export async function uploadAdminAsset(file: File) {
  const path = `${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}-${file.name}`;
  const client = requireSupabase();
  const { data, error } = await client.storage.from('techenabler-assets').upload(path, file, {
    upsert: false,
    contentType: file.type,
  });
  if (error) return { data: null, error };

  const { data: publicData } = client.storage.from('techenabler-assets').getPublicUrl(path);
  return {
    data: {
      path: data.path,
      publicUrl: publicData.publicUrl,
    },
    error: null,
  };
}

// --- Registro de atividades (activity_log) ---

export type ActivityAction =
  | 'login'
  | 'login_failed'
  | 'logout'
  | 'session_expired'
  | 'create'
  | 'update'
  | 'delete'
  | 'upload';

export interface ActivityLogEntry {
  id: string;
  created_at: string;
  actor_email: string | null;
  action: ActivityAction;
  entity: string | null;
  entity_label: string | null;
  severity: string;
  details: Record<string, unknown>;
}

interface LogActivityInput {
  action: ActivityAction;
  entity?: string;
  entityLabel?: string;
  severity?: 'info' | 'warning';
  details?: Record<string, unknown>;
  actorEmail?: string;
}

// Registra um evento. Best-effort: nunca lanca erro que quebre a acao do usuario.
export async function logActivity(input: LogActivityInput): Promise<void> {
  try {
    const client = requireSupabase();

    let actorEmail = input.actorEmail || null;
    let actorId: string | null = null;
    if (!actorEmail) {
      const { data } = await client.auth.getUser();
      actorEmail = data.user?.email || null;
      actorId = data.user?.id || null;
    }

    await client.from('activity_log').insert({
      action: input.action,
      entity: input.entity || null,
      entity_label: input.entityLabel || null,
      severity: input.severity || 'info',
      details: input.details || {},
      actor_email: actorEmail,
      actor_id: actorId,
    } as never);
  } catch {
    // Silencioso de proposito: o log nao deve interromper o fluxo.
  }
}

export async function listActivityLog(limit = 200): Promise<ActivityLogEntry[]> {
  const { data, error } = await requireSupabase()
    .from('activity_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error || !data) return [];
  return data as ActivityLogEntry[];
}
